import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Share2, Calendar, MapPin, Users, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { StatusChip, TYPE_LABELS } from '@/components/EventCard';
import { useAuthStore } from '@/lib/store';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ShareLinkDialog } from '@/components/ShareLinkDialog';

import OverviewTab from '@/pages/eventTabs/OverviewTab';
import ItineraryTab from '@/pages/eventTabs/ItineraryTab';
import MapTab from '@/pages/eventTabs/MapTab';
import ParticipantsTab from '@/pages/eventTabs/ParticipantsTab';
import ChatTab from '@/pages/eventTabs/ChatTab';
import MediaTab from '@/pages/eventTabs/MediaTab';
import PollsTab from '@/pages/eventTabs/PollsTab';
import QATab from '@/pages/eventTabs/QATab';
import ReviewsTab from '@/pages/eventTabs/ReviewsTab';
import ExpensesTab from '@/pages/eventTabs/ExpensesTab';

export default function EventDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('media');
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/events/${id}`);
      setEvent(r.data);
    } catch (err) {
      toast.error('Event not accessible');
      navigate('/events');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const isOrganizer = !!event && (event.owner_id === user?.id || user?.is_admin);

  const handleDelete = async () => {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    try { await api.delete(`/events/${id}`); toast.success('Deleted'); navigate('/events'); } catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="h-64 rounded-2xl bg-secondary animate-pulse" />;
  if (!event) return null;

  return (
    <div data-testid="event-detail-page">
      <div className="flex items-center gap-3 mb-3">
        <button onClick={() => navigate(-1)} data-testid="event-back-button" className="h-9 w-9 rounded-xl border bg-card flex items-center justify-center"><ChevronLeft className="h-4 w-4" /></button>
        <h1 className="font-serif text-2xl font-semibold leading-tight line-clamp-1 flex-1">{event.title}</h1>
        {isOrganizer && <Button variant="ghost" size="icon" onClick={() => setShareOpen(true)} data-testid="event-header-share-button" className="rounded-xl"><Share2 className="h-4 w-4" /></Button>}
        {isOrganizer && <Button variant="ghost" size="icon" onClick={handleDelete} data-testid="event-delete-button" className="rounded-xl text-destructive"><Trash2 className="h-4 w-4" /></Button>}
      </div>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="event-header" className="relative rounded-2xl overflow-hidden h-44 sm:h-56 mb-3 border">
        {event.cover_image ? <img src={event.cover_image} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 gradient-signature" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 to-transparent" />
        <div className="absolute top-3 left-3 flex gap-2">
          <Badge variant="outline" className="text-[10px] uppercase font-semibold tracking-wider bg-black/40 backdrop-blur text-white border-white/20">{TYPE_LABELS[event.type]}</Badge>
          <StatusChip status={event.status} />
        </div>
        <div className="absolute bottom-3 left-3 right-3 text-white">
          <div className="font-serif text-2xl font-semibold leading-tight">{event.title}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs sm:text-sm">
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{formatRange(event.start_date, event.end_date)}</span>
            {event.location_text && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{event.location_text}</span>}
            <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{event.participant_count}</span>
          </div>
        </div>
      </motion.div>

      <Tabs value={tab} onValueChange={setTab} data-testid="event-detail-tabs">
        <TabsList className="w-full overflow-x-auto whitespace-nowrap rounded-2xl bg-secondary p-1 no-scrollbar mb-3 flex justify-start">
          {[
            ['media','Media'],['overview','Overview'],['itinerary','Itinerary'],['map','Map'],['participants','People'],
            ['chat','Chat'],['polls','Polls'],['qa','Q&A'],['reviews','Reviews'],['expenses','Expenses'],
          ].map(([v,l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-xl px-3 py-1.5 text-sm data-[state=active]:bg-card data-[state=active]:shadow-sm">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview"><OverviewTab event={event} isOrganizer={isOrganizer} reload={load} /></TabsContent>
        <TabsContent value="itinerary"><ItineraryTab event={event} isOrganizer={isOrganizer} /></TabsContent>
        <TabsContent value="map"><MapTab event={event} /></TabsContent>
        <TabsContent value="participants"><ParticipantsTab event={event} isOrganizer={isOrganizer} /></TabsContent>
        <TabsContent value="chat"><ChatTab event={event} user={user} /></TabsContent>
        <TabsContent value="media"><MediaTab event={event} user={user} /></TabsContent>
        <TabsContent value="polls"><PollsTab event={event} user={user} isOrganizer={isOrganizer} /></TabsContent>
        <TabsContent value="qa"><QATab event={event} user={user} /></TabsContent>
        <TabsContent value="reviews"><ReviewsTab event={event} user={user} /></TabsContent>
        <TabsContent value="expenses"><ExpensesTab event={event} user={user} /></TabsContent>
      </Tabs>
      <ShareLinkDialog open={shareOpen} onOpenChange={setShareOpen} type="event" targetId={event.id} title={event.title} />
    </div>
  );
}

function formatRange(s, e) {
  try {
    const sd = new Date(s); const ed = new Date(e);
    const opts = { month: 'short', day: 'numeric', year: 'numeric' };
    if (sd.getFullYear() === ed.getFullYear() && sd.getMonth() === ed.getMonth() && sd.getDate() === ed.getDate()) {
      return sd.toLocaleDateString(undefined, opts);
    }
    return `${sd.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${ed.toLocaleDateString(undefined, opts)}`;
  } catch (err) { return ''; }
}
